import React, { useEffect, useState, useContext } from "react";
import {
  Checkbox,
  Box,
  Button,
  IconButton,
  Modal,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Reorder } from "framer-motion";
import {
  AlignLeft,
  Bookmark,
  BookmarkSimple,
  Browsers,
  Cards,
  CheckSquareOffset,
  CirclesThreePlus,
  Clock,
  Cube,
  FilmSlate,
  FilmStrip,
  Folders,
  FolderSimpleUser,
  FolderStar,
  Hexagon,
  Rectangle,
  Stack,
  Tag,
  Triangle,
  UserFocus,
  UserList,
  X,
  CaretDown,
  Check,
  DotsSixVertical,
  PencilSimple,
  Plus,
  PushPin,
  PushPinSimple,
  Trash,
} from "phosphor-react";
import { fget, fpost } from "../../../../API/callsAPI";
import { MovieColabContext } from "../../../../Context/MovieColabContext";
import CustomDropdown from "../../../Utilities/CustomDropdown/CustomDropdown.component";
import { EpisodesIconCustom } from "../../../Utilities/Svg/EpisodesIcon";
import { ProjectMediaIconCustom } from "../../../Utilities/Svg/ProjectMediaIcon";
import { ReviewerIcon } from "../../../Utilities/Svg/Reviewer";
import useStyles from "./NavigationSettingsCommon.styles";
import AddCustomPageModal from "../NavigationSettingsModal/AddACustomPage";
import DeleteNavConfig from "../NavigationSettingsModal/DeleteNavCofig";
import NewPageGroupModal from "../NavigationSettingsModal/NewPageGroupModal";

const PAGE_ICONS = {
  updates: Clock,
  tasks: CheckSquareOffset,
  assets: Cube,
  episodes: EpisodesIconCustom,
  sequences: FilmStrip,
  shots: FilmSlate,
  reviews: ReviewerIcon,
  custom_pages: Cards,
  project_media: ProjectMediaIconCustom,
  playlist: FolderStar,
};

const GROUP_ICON_OPTIONS = [
  { id: "circles-three-plus", icon: CirclesThreePlus },
  { id: "folders", icon: Folders },
  { id: "stack", icon: Stack },
  { id: "align-left", icon: AlignLeft },
  { id: "user-list", icon: UserList },
  { id: "user-focus", icon: UserFocus },
  { id: "folder-simple-user", icon: FolderSimpleUser },
  { id: "browsers", icon: Browsers },
  { id: "bookmark-simple", icon: BookmarkSimple },
  { id: "bookmark", icon: Bookmark },
  { id: "tag", icon: Tag },
  { id: "triangle", icon: Triangle },
  { id: "rectangle", icon: Rectangle },
  { id: "hexagon", icon: Hexagon },
];

const PageIcon = ({ icon: Icon, size = 18 }) => {
  if (!Icon) {
    return (
      <span
        style={{
          width: size,
          height: size,
          borderRadius: "3px",
          border: "1px solid #606479",
          display: "inline-block",
          flexShrink: 0,
        }}
      />
    );
  }

  return <Icon size={size} color="#B1B5C9" />;
};

const GroupIcon = ({ icon: Icon, size = 16 }) => {
  if (!Icon) {
    return <CirclesThreePlus size={size} color="#B1B5C9" />;
  }
  return <Icon size={size} color="#B1B5C9" />;
};

const getGroupIcon = (iconId) => {
  return (
    GROUP_ICON_OPTIONS.find((option) => option.id === iconId)?.icon ||
    CirclesThreePlus
  );
};

const toPageGroupEditData = (group) => ({
  ...group,
  label: group.name,
  iconId: group.icon,
  pageIds: group.items?.map((item) => item.key) ?? [],
});

const NavGroupCard = ({ group, onEdit, onDelete, onToggle }) => {
  const classes = useStyles();

  return (
    <div
      className={`${classes.groupCard} ${
        group.enabled ? classes.groupCardEnabled : classes.groupCardDisabled
      }`}
      onClick={() => onToggle(group.key)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onToggle(group.key);
        }
      }}
    >
      <div className={classes.groupCardHeader}>
        <div className={classes.groupCardTitleRow}>
          <GroupIcon
            icon={getGroupIcon(group.icon)}
            size={16}
            color="#B1B5C9"
          />
          <Typography fontSize="14px !important">{group.name}</Typography>
        </div>
        <div className={classes.groupCardActions}>
          <IconButton
            size="small"
            className={classes.editButton}
            onClick={(event) => {
              event.stopPropagation();
              onEdit?.();
            }}
          >
            <PencilSimple size={16} />
          </IconButton>
          <IconButton
            size="small"
            className={classes.deleteButton}
            onClick={(event) => {
              event.stopPropagation();
              onDelete?.();
            }}
          >
            <Trash size={16} />
          </IconButton>
        </div>
      </div>
      <Typography className={classes.groupPagesList}>
        {group.items.map((item) => item.label).join(", ")}
      </Typography>
    </div>
  );
};

const PageVisibilityCard = ({ page, onToggle }) => {
  const classes = useStyles();
  return (
    <div
      className={`${classes.pageCard} ${
        page.enabled ? classes.pageCardEnabled : classes.pageCardDisabled
      }`}
      onClick={() => onToggle(page.key)}
      role="button"
      tabIndex={0}
    >
      <div className={classes.pageCardContent}>
        <PageIcon icon={PAGE_ICONS[page.key]} />
        <Typography className={classes.pageLabel}>{page.label}</Typography>
      </div>
      <Checkbox
        checked={page.enabled}
        size="small"
        sx={{
          padding: "3px",
          color: "#606479",
          "&.Mui-checked": {
            color: "#6C65D9",
          },
        }}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        onChange={() => onToggle(page.key)}
      />
    </div>
  );
};

const NavigationSettingsCommon = ({
  pages,
  customPages,
  navGroups,
  navOrder,
  showPageNames,
  setShowPageNames,
  setSnackValue,
  projectId,
  handlePageToggle,
  handleCustomPageToggle,
  handleGroupToggle,
  handleNavReorder,
  handleAddCustomPage,
  handleDeleteCustomPage,
  handleAddPageGroup,
  handleEditPageGroup,
  handleDeletePageGroup,
}) => {
  const classes = useStyles();
  const [isCustomPageModalOpen, setIsCustomPageModalOpen] = useState(false);
  const [isDeleteCustomPageModalOpen, setIsDeleteCustomPageModalOpen] =
    useState({ open: false, customPage: null });
  const [pageGroupModal, setPageGroupModal] = useState({
    open: false,
    editData: null,
  });
  const [isDeleteGroupModalOpen, setIsDeleteGroupModalOpen] = useState({
    open: false,
    group: null,
  });

  const handlePageGroupSubmit = (data) => {
    if (pageGroupModal.editData) {
      handleEditPageGroup({
        ...data,
        groupKey: pageGroupModal.editData.key,
      });
      setPageGroupModal({ open: false, editData: null });
      return;
    }

    handleAddPageGroup(data);
  };
  return (
    <>
      <Typography fontSize="14px !important">
        Choose which pages appear in your navigation bar
      </Typography>
      <div className={classes.pageGrid}>
        {pages.map((page) => (
          <PageVisibilityCard
            key={page.key}
            page={page}
            onToggle={handlePageToggle}
            classes={classes}
          />
        ))}
      </div>
      <div>
        <Typography className={classes.sectionSubtitle}>
          Saved custom pages
        </Typography>
        <div className={classes.customPagesRow}>
          <button
            type="button"
            className={classes.dashedButton}
            onClick={() => setIsCustomPageModalOpen(true)}
          >
            <Plus size={14} />
            Add a Custom Page
          </button>
          {customPages.map((page) => (
            <div
              key={page.key}
              className={`${classes.customPageChip} ${page.enabled ? classes.customPageChipEnabled : classes.customPageChipDisabled}`}
              onClick={() => handleCustomPageToggle(page.key)}
            >
              <div className={classes.pageCardContent}>
                <PushPinSimple size={14} color="#f9f9f9" />
                <Typography fontSize="12px !important">{page.label}</Typography>
              </div>
              <IconButton
                size="small"
                className={classes.deleteButton}
                onClick={(event) => {
                  event.stopPropagation();
                  setIsDeleteCustomPageModalOpen({
                    open: true,
                    customPage: page,
                  });
                }}
              >
                <Trash size={14} />
              </IconButton>
            </div>
          ))}
        </div>
      </div>
      <div>
        <Typography fontSize="14px !important">
          Collapsed navigation buttons
        </Typography>
        <Typography className={classes.sectionSubtitle}>
          Group related pages under one button to keep the nav bar tidy
        </Typography>
        <div className={classes.groupsRow}>
          <button
            type="button"
            className={classes.newGroupCard}
            onClick={() => setPageGroupModal({ open: true, editData: null })}
          >
            <Plus size={16} />
            New Group
          </button>
          {navGroups.map((group) => (
            <NavGroupCard
              key={group.key}
              group={group}
              onToggle={handleGroupToggle}
              classes={classes}
              onEdit={() =>
                setPageGroupModal({
                  open: true,
                  editData: toPageGroupEditData(group),
                })
              }
              onDelete={() => setIsDeleteGroupModalOpen({ open: true, group })}
            />
          ))}
        </div>
      </div>
      <div className={classes.toggleRow}>
        <div>
          <Typography fontSize="14px !important">Show page names</Typography>
          <Typography className={classes.sectionSubtitle}>
            Page names appear in the nav bar
          </Typography>
        </div>
        <Switch
          checked={showPageNames}
          onChange={(event) => setShowPageNames(event.target.checked)}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": { color: "#6C65D9" },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              background: "#6C65D9CC",
            },
          }}
        />
      </div>
      <div>
        <Typography fontSize="14px !important">
          Reorder navigation bar
        </Typography>
        <div className={classes.reorderContainer}>
          <Reorder.Group
            axis="x"
            values={navOrder}
            onReorder={handleNavReorder}
            role="list"
            className={classes.reorderGroup}
          >
            {navOrder.map((item) => (
              <Reorder.Item
                key={item.key}
                value={item}
                drag
                role="listitem"
                className={classes.reorderItem}
                whileDrag={{ scale: 1.03, zIndex: 1000 }}
              >
                {item.type === "page" && (
                  <PageIcon icon={PAGE_ICONS[item.key]} />
                )}
                {item.type === "customPage" && (
                  <Cards size={16} color="#B1B5C9" />
                )}
                {item.type === "group" && (
                  <GroupIcon
                    icon={getGroupIcon(item.icon)}
                    size={16}
                    color="#B1B5C9"
                  />
                )}
                <Typography className={classes.reorderItemLabel}>
                  {item.type === "group" ? item.name : item.label}
                </Typography>
                {/* {item.type === "group" && (
                  <CaretDown size={14} color="#606479" />
                )} */}
                <DotsSixVertical size={16} color="#606479" />
              </Reorder.Item>
            ))}
          </Reorder.Group>
        </div>
      </div>
      <Modal
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        open={pageGroupModal.open}
        onClose={() => setPageGroupModal({ open: false, editData: null })}
      >
        <NewPageGroupModal
          handleClose={() => setPageGroupModal({ open: false, editData: null })}
          setSnackValue={setSnackValue}
          editMode={Boolean(pageGroupModal.editData)}
          initialData={pageGroupModal.editData}
          onSubmit={handlePageGroupSubmit}
          pages={pages}
          customPages={customPages}
        />
      </Modal>
      <Modal
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        open={isCustomPageModalOpen}
        onClose={() => setIsCustomPageModalOpen(false)}
      >
        <AddCustomPageModal
          handleClose={() => setIsCustomPageModalOpen(false)}
          onSubmit={handleAddCustomPage}
          setSnackValue={setSnackValue}
          projectId={projectId}
          customPages={customPages}
        />
      </Modal>
      <Modal
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        open={isDeleteCustomPageModalOpen.open}
        onClose={() =>
          setIsDeleteCustomPageModalOpen({ open: false, customPage: null })
        }
      >
        <DeleteNavConfig
          handleDeleteNavConfig={() => {
            setIsDeleteCustomPageModalOpen({ open: false, customPage: null });
            handleDeleteCustomPage(isDeleteCustomPageModalOpen.customPage.key);
          }}
          handleCloseModal={() =>
            setIsDeleteCustomPageModalOpen({ open: false, customPage: null })
          }
          modalTitle="Delete Custom Page"
          modalDescription="Are you sure you want to delete this custom page from your navigation bar?"
        />
      </Modal>
      <Modal
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        open={isDeleteGroupModalOpen.open}
        onClose={() => setIsDeleteGroupModalOpen({ open: false, group: null })}
      >
        <DeleteNavConfig
          handleDeleteNavConfig={() => {
            setIsDeleteGroupModalOpen({ open: false, group: null });
            handleDeletePageGroup(isDeleteGroupModalOpen.group.key);
          }}
          handleCloseModal={() =>
            setIsDeleteGroupModalOpen({ open: false, group: null })
          }
          modalTitle="Delete Page Group"
          modalDescription="Are you sure you want to delete this page group from your navigation bar?"
        />
      </Modal>
    </>
  );
};

export default NavigationSettingsCommon;
